import axios from "axios";
import Cookies from "js-cookie";
import {
  setAccessToken,
  setRefreshToken,
  showSessionExpiredModal,
} from "../slices/authSlice";
import getTenantId from "../../components/Tenent";


const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_ESG}/api`,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const parsedURL = new URL(window.location.href);
      const subdomain = parsedURL?.hostname?.split(".")?.[0];
      const tenantId = getTenantId();

      if (subdomain === "localhost") {
        config.headers["x-tenant-id"] = tenantId;
      } else {
        config.baseURL = config.baseURL?.replace("www", subdomain);
        config.headers["x-tenant-id"] = subdomain;
      }

      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;

        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");

        if (!refreshToken || !accessToken) {
          dispatch(setAccessToken(null));
          dispatch(setRefreshToken(null));
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const parsedURL = new URL(window.location.href);
          const subdomain = parsedURL?.hostname?.split(".")?.[0];
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": subdomain === "localhost" ? tenantId : subdomain, // Ensure x-tenant-id is set
          };
          // Request new tokens from `/auth/core/refresh`
          const { data } = await axios.post(
            `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders }
          );
          console.log(data?.refresh_token);
          console.log(data?.access_token);
          // Save new tokens in Redux
          dispatch(setAccessToken(data?.access_token));
          dispatch(setRefreshToken(data?.refresh_token));

          // Retry the original request with the new token
          originalConfig.headers[
            "Authorization"
          ] = `Bearer ${data?.access_token}`;
          originalConfig.headers["Refresh"] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          dispatch(setAccessToken(null));
          dispatch(setRefreshToken(null));
          return Promise.reject(_error);
        }
      }

      if (
        err.response?.status === 401 &&
        errorMessage.includes("session active elsewhere")
      ) {
        dispatch(showSessionExpiredModal());
      }

      return Promise.reject(err);
    }
  );
};

const axiosBaseQuery =
  () =>
  async (
    { responseType, url, method, body, params, ...requestOpts },
    { getState, dispatch }
  ) => {
    setUpInterceptors(getState, dispatch);
    try {
      const axiosOptions = {
        url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
        responseType: responseType || undefined,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data || axiosError.message,
        },
      };
    }
  };

export default axiosBaseQuery();
